import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { useAdminApi } from "@/services/api/admin-api";
import type { MailboxConnectionStatus } from "@/types/api/admin";

interface OAuthConnectButtonProps {
    clientId: number;
    sourceId: number;
    connectionStatus: MailboxConnectionStatus;
    onConnected: () => void;
}

export function OAuthConnectButton({ clientId, sourceId, connectionStatus, onConnected }: OAuthConnectButtonProps) {
    const adminApi = useAdminApi();
    const popupRef = useRef<Window | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        return () => {
            if (intervalRef.current != null) clearInterval(intervalRef.current);
        };
    }, []);

    async function handleConnect() {
        setIsLoading(true);
        try {
            const url = await adminApi.getEmailSourceConnectUrl(clientId, sourceId);
            popupRef.current = window.open(url, "oauth-popup", "width=600,height=700,left=200,top=100");

            if (intervalRef.current != null) clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
                if (popupRef.current?.closed) {
                    if (intervalRef.current != null) clearInterval(intervalRef.current);
                    onConnected();
                }
            }, 500);
        } catch (err) {
            alert(err instanceof Error ? err.message : "Failed to start OAuth connection. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }

    const label = connectionStatus === "Connected" ? "Reconnect" : "Connect";

    return (
        <Button color="secondary" size="sm" isDisabled={isLoading} onClick={handleConnect}>
            {isLoading ? "Opening..." : label}
        </Button>
    );
}
